import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import { BottomNav } from "../../components/BottomNav";
import { SourceBadge } from "../../components/SourceBadge";
import { useAuth } from "../../store/auth";
import type {
  ImportBatch,
  OverviewResponse,
  PendingReceiptOut,
  ReceiptUploadResponse,
  TransactionListResponse,
  UserOverviewRow,
} from "../../types";
import { formatDate, formatMoney, isMatchReady } from "../../lib/format";

type DetailTab = "open" | "matched" | "pending";

export function AdminOverviewPage() {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const [overrulingId, setOverrulingId] = useState<number | null>(null);
  const [overruleError, setOverruleError] = useState<string | null>(null);
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [period, setPeriod] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<UserOverviewRow | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>("open");
  const [detail, setDetail] = useState<TransactionListResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pending, setPending] = useState<PendingReceiptOut[]>([]);
  const [pendingLoading, setPendingLoading] = useState(true);

  useEffect(() => {
    const qs = period ? `?period=${encodeURIComponent(period)}` : "";
    api
      .request<OverviewResponse>(`/transactions/overview${qs}`)
      .then((data) => {
        setOverview(data);
        setSelectedUser(null);
        setDetail(null);
      })
      .catch(console.error);
  }, [period]);

  async function overrulePending(item: PendingReceiptOut) {
    const txnId = item.receipt.already_matched_transaction_id;
    if (!txnId) return;
    if (
      !confirm(
        `This looks like the receipt already on transaction #${txnId}. Overrule that and match this copy to another card line? The existing match stays.`,
      )
    ) {
      return;
    }
    setOverrulingId(item.receipt.id);
    setOverruleError(null);
    try {
      const fresh = await api.request<ReceiptUploadResponse>(
        `/receipts/${item.receipt.id}/overrule`,
        { method: "POST" },
      );
      const raw = JSON.stringify(fresh);
      sessionStorage.setItem("lastUpload", raw);
      try {
        localStorage.setItem("lastUpload", raw);
      } catch {
        /* ignore */
      }
      navigate("/match");
    } catch (err) {
      setOverruleError(err instanceof Error ? err.message : "Could not overrule");
      setOverrulingId(null);
    }
  }

  const periodOptions = useMemo(() => {
    return overview?.periods.map((p) => p.period) ?? [];
  }, [overview]);

  const [allPeriods, setAllPeriods] = useState<string[]>([]);
  const [statements, setStatements] = useState<ImportBatch[]>([]);
  useEffect(() => {
    api
      .request<OverviewResponse>("/transactions/overview")
      .then((data) => setAllPeriods(data.periods.map((p) => p.period)))
      .catch(console.error);
    api
      .request<ImportBatch[]>("/imports")
      .then(setStatements)
      .catch(console.error);
  }, []);

  async function loadPending() {
    setPendingLoading(true);
    try {
      const list = await api.request<PendingReceiptOut[]>(
        "/receipts?pending_only=true&include_suggestions=true",
      );
      setPending(list);
    } catch (e) {
      console.error(e);
    } finally {
      setPendingLoading(false);
    }
  }

  useEffect(() => {
    void loadPending();
  }, []);

  useEffect(() => {
    if (!selectedUser?.user_id) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    const statuses =
      detailTab === "open"
        ? ["insufficient", "unmatched", "suggested"]
        : ["matched", "excluded"];
    const qs = new URLSearchParams({
      user_id: String(selectedUser.user_id),
      limit: "50",
    });
    if (period) qs.set("period", period);
    Promise.all(
      statuses.map((status) =>
        api.request<TransactionListResponse>(`/transactions?${qs}&status=${status}`)
      )
    )
      .then((results) => {
        const items = results.flatMap((r) => r.items);
        items.sort((a, b) => b.transaction_date.localeCompare(a.transaction_date));
        setDetail({
          items,
          total: items.length,
          unmatched: 0,
          matched: 0,
          suggested: 0,
        });
      })
      .catch(console.error)
      .finally(() => setDetailLoading(false));
  }, [selectedUser, detailTab, period]);

  const totals = overview?.totals;
  const amounts = overview?.amount_totals;
  const users = overview?.by_user ?? [];
  const openCount = (totals?.unmatched ?? 0) + (totals?.suggested ?? 0);
  const pendingAlready = pending.filter(
    (p) => p.receipt.already_matched_transaction_id,
  ).length;
  const pendingReady = pending.filter(
    (p) =>
      !p.receipt.already_matched_transaction_id && isMatchReady(p.suggestions),
  ).length;
  const pendingWaiting = pending.length - pendingReady - pendingAlready;
  const pendingByUser = useMemo(() => {
    const map = new Map<number, PendingReceiptOut[]>();
    for (const item of pending) {
      const uid = item.receipt.uploaded_by_id;
      const list = map.get(uid) ?? [];
      list.push(item);
      map.set(uid, list);
    }
    return map;
  }, [pending]);
  const selectedPending = selectedUser?.user_id
    ? pendingByUser.get(selectedUser.user_id) ?? []
    : [];

  return (
    <div className="app-shell admin">
      <header className="topbar">
        <div className="brand-mark">
          <div className="logo">R</div>
          <div>
            <div className="name">Receipt Matching</div>
            <div className="sub">Admin · {user?.full_name}</div>
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" type="button" onClick={logout}>
          Log out
        </button>
      </header>

      <div className="row between" style={{ marginBottom: "0.85rem", gap: "0.75rem", flexWrap: "wrap" }}>
        <div className="field" style={{ margin: 0, minWidth: "10rem" }}>
          <label>Period</label>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="">All periods</option>
            {(allPeriods.length ? allPeriods : periodOptions).map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div className="row" style={{ gap: "0.5rem", alignItems: "flex-end" }}>
          <Link to="/capture" className="btn btn-accent btn-sm">
            Scan receipt
          </Link>
          <Link to="/admin/import" className="btn btn-primary btn-sm">
            Import CSV
          </Link>
          <Link to="/admin/export" className="btn btn-ghost btn-sm">
            Export
          </Link>
        </div>
      </div>

      <div className="stat-grid" style={{ marginBottom: "1.25rem" }}>
        <div className="stat">
          <div className="n">{openCount}</div>
          <div className="l">Open (unmatched + suggested)</div>
          <div className="muted" style={{ fontSize: "0.8rem", marginTop: "0.25rem" }}>
            {amounts?.unmatched != null
              ? formatMoney(amounts.unmatched)
              : "—"}{" "}
            unmatched
          </div>
          {!!totals?.insufficient && (
            <div className="muted" style={{ fontSize: "0.8rem" }}>
              {totals.insufficient} flagged insufficient
            </div>
          )}
        </div>
        <div className="stat">
          <div className="n">{totals?.matched ?? "—"}</div>
          <div className="l">Matched</div>
          <div className="muted" style={{ fontSize: "0.8rem", marginTop: "0.25rem" }}>
            {amounts?.matched != null ? formatMoney(amounts.matched) : "—"}
          </div>
        </div>
        <div className="stat">
          <div className="n">{pendingLoading ? "…" : pending.length}</div>
          <div className="l">Pending scans</div>
          <div className="muted" style={{ fontSize: "0.8rem", marginTop: "0.25rem" }}>
            {pendingReady} ready · {pendingWaiting} waiting
            {pendingAlready > 0 ? ` · ${pendingAlready} already matched` : ""}
          </div>
        </div>
        <div className="stat">
          <div className="n">{totals?.total ?? "—"}</div>
          <div className="l">Total transactions</div>
          <div className="muted" style={{ fontSize: "0.8rem", marginTop: "0.25rem" }}>
            {amounts?.total != null ? formatMoney(amounts.total) : "—"}
          </div>
        </div>
      </div>

      <section className="panel stack" style={{ marginBottom: "1rem" }}>
        <div className="row between">
          <h2 style={{ fontSize: "1.15rem", margin: 0 }}>Pending scans</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void loadPending()}
            disabled={pendingLoading}
          >
            {pendingLoading ? "Loading…" : "Refresh"}
          </button>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
          Photos and PDFs uploaded by employees that are not matched to a card line yet.
        </p>
        {!pendingLoading && pending.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>No pending scans.</p>
        )}
        {overruleError && <div className="alert">{overruleError}</div>}
        {pending.map((item) => {
          const { receipt, extraction, suggestions } = item;
          const alreadyTxn = receipt.already_matched_transaction_id ?? null;
          const ready = !alreadyTxn && isMatchReady(suggestions);
          const hasSuggestion = !alreadyTxn && suggestions.length > 0;
          const isPdf =
            (receipt.content_type || "").includes("pdf") ||
            receipt.original_filename.toLowerCase().endsWith(".pdf");
          return (
            <div key={receipt.id} className="txn-card" style={{ width: "100%" }}>
            <Link
              to={`/receipts/${receipt.id}`}
              state={{ siblingIds: pending.map((p) => p.receipt.id) }}
              style={{
                display: "block",
                textDecoration: "none",
                color: "inherit",
                width: "100%",
              }}
            >
              <div className="row between">
                <div className="merchant">
                  {extraction.merchant || receipt.extracted_merchant || receipt.original_filename}
                </div>
                <span
                  className={
                    alreadyTxn
                      ? "badge badge-matched"
                      : ready
                        ? "badge badge-suggested"
                        : "badge badge-unmatched"
                  }
                >
                  {alreadyTxn
                    ? "Already matched"
                    : ready
                      ? "Match ready"
                      : hasSuggestion
                        ? "Possible match"
                        : "Waiting"}
                </span>
              </div>
              <div className="row between" style={{ marginTop: "0.35rem" }}>
                <span className="muted" style={{ fontSize: "0.85rem" }}>
                  {item.uploaded_by_name || "Unknown user"}
                  {" · "}
                  {isPdf ? "PDF" : "Photo"}
                  {" · "}
                  {extraction.date
                    ? formatDate(String(extraction.date))
                    : formatDate(receipt.created_at)}
                  {(extraction.reference || receipt.extracted_reference) && (
                    <>
                      {" · "}
                      <span className="mono">
                        {extraction.reference || receipt.extracted_reference}
                      </span>
                    </>
                  )}
                </span>
                <span className="amount">
                  {extraction.amount != null
                    ? formatMoney(String(extraction.amount), extraction.currency || "EUR")
                    : "—"}
                </span>
              </div>
              {alreadyTxn && (
                <div className="muted" style={{ marginTop: "0.35rem", fontSize: "0.8rem" }}>
                  Same receipt is already on transaction #{alreadyTxn}
                </div>
              )}
            </Link>
            {alreadyTxn && (
              <div className="row" style={{ marginTop: "0.55rem", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn-accent btn-sm"
                  disabled={overrulingId === receipt.id}
                  onClick={() => overrulePending(item)}
                >
                  {overrulingId === receipt.id ? "Opening…" : "Overrule"}
                </button>
              </div>
            )}
            </div>
          );
        })}
      </section>

      <div className="admin-grid">
        <section className="panel stack">
          <h2 style={{ fontSize: "1.15rem" }}>Per employee</h2>
          {!users.length && <p className="muted">No transactions for this filter.</p>}
          {!!users.length && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Open</th>
                    <th>Matched</th>
                    <th>Open €</th>
                    <th>Matched €</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const open = u.unmatched + u.suggested + (u.insufficient ?? 0);
                    const userPendingCount = u.user_id != null
                      ? pendingByUser.get(u.user_id)?.length ?? 0
                      : 0;
                    const active = selectedUser?.label === u.label && selectedUser?.user_id === u.user_id;
                    return (
                      <tr key={`${u.user_id ?? "x"}-${u.label}`} className={active ? "row-active" : undefined}>
                        <td>
                          <strong>{u.label}</strong>
                          <div className="muted" style={{ fontSize: "0.78rem" }}>
                            {u.total} total · {u.suggested} suggested
                            {u.insufficient ? ` · ${u.insufficient} insufficient` : ""}
                            {userPendingCount > 0 ? ` · ${userPendingCount} pending scan(s)` : ""}
                          </div>
                        </td>
                        <td>{open}</td>
                        <td>{u.matched}</td>
                        <td>{formatMoney(u.amount_unmatched)}</td>
                        <td>{formatMoney(u.amount_matched)}</td>
                        <td>
                          {u.user_id != null ? (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => {
                                setSelectedUser(u);
                                if (open > 0) setDetailTab("open");
                                else if (userPendingCount > 0) setDetailTab("pending");
                                else setDetailTab("matched");
                              }}
                            >
                              Details
                            </button>
                          ) : (
                            <span className="muted">Unmapped</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="panel stack">
          <div className="row between" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
            <h2 style={{ fontSize: "1.15rem", margin: 0 }}>
              {selectedUser ? selectedUser.label : "Transaction detail"}
            </h2>
            {selectedUser && (
              <div className="seg">
                <button
                  type="button"
                  className={detailTab === "open" ? "active" : ""}
                  onClick={() => setDetailTab("open")}
                >
                  Open
                </button>
                <button
                  type="button"
                  className={detailTab === "matched" ? "active" : ""}
                  onClick={() => setDetailTab("matched")}
                >
                  Done
                </button>
                <button
                  type="button"
                  className={detailTab === "pending" ? "active" : ""}
                  onClick={() => setDetailTab("pending")}
                >
                  Pending{selectedPending.length ? ` (${selectedPending.length})` : ""}
                </button>
              </div>
            )}
          </div>

          {!selectedUser && (
            <p className="muted">
              Select an employee to see transactions and pending scans.
            </p>
          )}
          {selectedUser && detailTab !== "pending" && detailLoading && (
            <p className="muted">Loading…</p>
          )}
          {selectedUser &&
            detailTab !== "pending" &&
            !detailLoading &&
            !detail?.items.length && (
              <p className="muted">No {detailTab} transactions for this employee.</p>
            )}
          {selectedUser &&
            detailTab !== "pending" &&
            !detailLoading &&
            detail?.items.map((t) => (
              <div key={t.id} className="row between" style={{ fontSize: "0.9rem" }}>
                <div>
                  <div className="row" style={{ gap: "0.4rem", alignItems: "center" }}>
                    <div style={{ fontWeight: 600 }}>{t.merchant_name}</div>
                    <SourceBadge source={t.import_source} />
                  </div>
                  <div className="muted">
                    {t.transaction_date} · {t.status}
                    {t.import_source === "boekhoudgemak" ? " · bank" : ""}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div>{formatMoney(t.amount, t.currency)}</div>
                  <Link
                    to={`/transactions/${t.id}`}
                    state={{ siblingIds: detail.items.map((x) => x.id) }}
                    className="muted"
                  >
                    View
                  </Link>
                </div>
              </div>
            ))}

          {selectedUser && detailTab === "pending" && overruleError && (
            <div className="alert">{overruleError}</div>
          )}
          {selectedUser && detailTab === "pending" && selectedPending.length === 0 && (
            <p className="muted">No pending scans for this employee.</p>
          )}
          {selectedUser &&
            detailTab === "pending" &&
            selectedPending.map((item) => {
              const { receipt, extraction, suggestions } = item;
              const alreadyTxn = receipt.already_matched_transaction_id ?? null;
              const ready = !alreadyTxn && isMatchReady(suggestions);
              const hasSuggestion = !alreadyTxn && suggestions.length > 0;
              const isPdf =
                (receipt.content_type || "").includes("pdf") ||
                receipt.original_filename.toLowerCase().endsWith(".pdf");
              return (
                <div key={receipt.id} className="txn-card" style={{ width: "100%" }}>
                <Link
                  to={`/receipts/${receipt.id}`}
                  state={{ siblingIds: selectedPending.map((p) => p.receipt.id) }}
                  style={{
                    display: "block",
                    textDecoration: "none",
                    color: "inherit",
                    width: "100%",
                  }}
                >
                  <div className="row between">
                    <div className="merchant">
                      {extraction.merchant ||
                        receipt.extracted_merchant ||
                        receipt.original_filename}
                    </div>
                    <span
                      className={
                        alreadyTxn
                          ? "badge badge-matched"
                          : ready
                            ? "badge badge-suggested"
                            : "badge badge-unmatched"
                      }
                    >
                      {alreadyTxn
                        ? "Already matched"
                        : ready
                          ? "Match ready"
                          : hasSuggestion
                            ? "Possible match"
                            : "Waiting"}
                    </span>
                  </div>
                  <div className="row between" style={{ marginTop: "0.35rem" }}>
                    <span className="muted" style={{ fontSize: "0.85rem" }}>
                      {isPdf ? "PDF" : "Photo"}
                      {" · "}
                      {extraction.date
                        ? formatDate(String(extraction.date))
                        : formatDate(receipt.created_at)}
                      {(extraction.reference || receipt.extracted_reference) && (
                        <>
                          {" · "}
                          <span className="mono">
                            {extraction.reference || receipt.extracted_reference}
                          </span>
                        </>
                      )}
                    </span>
                    <span className="amount">
                      {extraction.amount != null
                        ? formatMoney(
                            String(extraction.amount),
                            extraction.currency || "EUR",
                          )
                        : "—"}
                    </span>
                  </div>
                  {alreadyTxn && (
                    <div className="muted" style={{ marginTop: "0.35rem", fontSize: "0.8rem" }}>
                      Same receipt is already on transaction #{alreadyTxn}
                    </div>
                  )}
                </Link>
                {alreadyTxn && (
                  <div className="row" style={{ marginTop: "0.55rem", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className="btn btn-accent btn-sm"
                      disabled={overrulingId === receipt.id}
                      onClick={() => overrulePending(item)}
                    >
                      {overrulingId === receipt.id ? "Opening…" : "Overrule"}
                    </button>
                  </div>
                )}
                </div>
              );
            })}
        </section>
      </div>

      {!!statements.length && (
        <section className="panel stack" style={{ marginTop: "1rem" }}>
          <div className="row between">
            <h2 style={{ fontSize: "1.15rem", margin: 0 }}>ING statements</h2>
            <Link to="/admin/import" className="btn btn-ghost btn-sm">
              Import
            </Link>
          </div>
          <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
            Labels match official PDF names (e.g. Statement-20260609).
          </p>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Statement</th>
                  <th>Cycle</th>
                  <th>Imported</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {statements.slice(0, 12).map((b) => (
                  <tr key={b.id}>
                    <td>
                      <code style={{ fontSize: "0.85rem" }}>{b.period_label || "—"}</code>
                    </td>
                    <td className="muted" style={{ fontSize: "0.85rem" }}>
                      {b.period_start && b.period_end
                        ? `${b.period_start} → ${b.period_end}`
                        : "—"}
                    </td>
                    <td>{b.imported_count}</td>
                    <td className="muted" style={{ fontSize: "0.85rem" }}>
                      {new Date(b.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!!overview?.periods.length && (
        <section className="panel stack" style={{ marginTop: "1rem" }}>
          <h2 style={{ fontSize: "1.15rem" }}>By calendar month</h2>
          {overview.periods.map((p) => {
            const pct = p.total ? Math.round((p.matched / p.total) * 100) : 0;
            const open = p.unmatched + p.suggested;
            return (
              <div key={p.period}>
                <div className="row between">
                  <strong>{p.period}</strong>
                  <span className="muted">
                    {p.matched}/{p.total} matched · {open} open · {formatMoney(p.amount_matched)} done
                  </span>
                </div>
                <div className="progress-bar" style={{ marginTop: "0.4rem" }}>
                  <span style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </section>
      )}

      <BottomNav />
    </div>
  );
}
